import { useState } from "react"

import { useTranslation } from "react-i18next"

import { ShieldCheck } from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "@/components/ui/menubar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

const SAMPLE_PERSONAL_SOURCES = [
  { id: "1", name: "My trusted blogs", url: "https://example.com/blogs" },
  { id: "2", name: "Research papers", url: "https://example.com/papers" },
]

const SAMPLE_COMPANY_SOURCES = [
  { id: "3", name: "Company Wiki", url: "https://wiki.company.com" },
  { id: "4", name: "Internal Docs", url: "https://docs.company.com" },
]

export function DemoTrustedSourcesMenu() {
  const { t } = useTranslation()
  const [selectedUrls, setSelectedUrls] = useState<string[]>([])
  const [trustedMode, setTrustedMode] = useState<"only" | "preferred">("preferred")

  const allSources = [...SAMPLE_PERSONAL_SOURCES, ...SAMPLE_COMPANY_SOURCES]
  const isAllSelected = selectedUrls.length === allSources.length

  const toggleSource = (url: string, checked: boolean) => {
    if (checked) {
      setSelectedUrls([...selectedUrls, url])
    } else {
      setSelectedUrls(selectedUrls.filter((u) => u !== url))
    }
  }

  const toggleAll = () => {
    if (isAllSelected) {
      setSelectedUrls([])
    } else {
      setSelectedUrls(allSources.map((s) => s.url))
    }
  }

  return (
    <Menubar className="border-none bg-transparent p-0 shadow-none">
      <MenubarMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <MenubarTrigger
              className={cn(
                buttonVariants({ size: "icon", variant: "icon" }),
                "!size-7 cursor-pointer !p-0",
                selectedUrls.length > 0 && "bg-success/15 text-success hover:bg-success/15"
              )}
            >
              <ShieldCheck className="size-5" />
            </MenubarTrigger>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
            <p>{t("sources.trusted_sources")}</p>
          </TooltipContent>
        </Tooltip>

        <MenubarContent align="start" className="z-50" side="top">
          <MenubarItem onClick={toggleAll} onSelect={(e) => e.preventDefault()}>
            {isAllSelected ? t("sources.disable_all_trusted") : t("sources.enable_all_trusted")}
          </MenubarItem>

          <MenubarSub>
            <MenubarSubTrigger>{t("sources.personal_sources")}</MenubarSubTrigger>
            <MenubarSubContent className="max-h-[150px] max-w-[250px] overflow-y-auto">
              {SAMPLE_PERSONAL_SOURCES.map((source) => (
                <MenubarCheckboxItem
                  key={source.id}
                  checked={selectedUrls.includes(source.url)}
                  onCheckedChange={(checked) => toggleSource(source.url, Boolean(checked))}
                  onSelect={(e) => e.preventDefault()}
                >
                  <span className="truncate">{source.name}</span>
                </MenubarCheckboxItem>
              ))}
            </MenubarSubContent>
          </MenubarSub>

          <MenubarSub>
            <MenubarSubTrigger>{t("sources.company_sources")}</MenubarSubTrigger>
            <MenubarSubContent className="max-h-[150px] max-w-[250px] overflow-y-auto">
              {SAMPLE_COMPANY_SOURCES.map((source) => (
                <MenubarCheckboxItem
                  key={source.id}
                  checked={selectedUrls.includes(source.url)}
                  onCheckedChange={(checked) => toggleSource(source.url, Boolean(checked))}
                  onSelect={(e) => e.preventDefault()}
                >
                  <span className="truncate">{source.name}</span>
                </MenubarCheckboxItem>
              ))}
            </MenubarSubContent>
          </MenubarSub>

          <MenubarSeparator />
          <MenubarSub>
            <MenubarSubTrigger>{t("sources.mode")}</MenubarSubTrigger>
            <MenubarSubContent>
              <MenubarCheckboxItem
                checked={trustedMode === "only"}
                onCheckedChange={() => setTrustedMode("only")}
                onSelect={(e) => e.preventDefault()}
              >
                {t("sources.trusted_only")}
              </MenubarCheckboxItem>
              <MenubarCheckboxItem
                checked={trustedMode === "preferred"}
                onCheckedChange={() => setTrustedMode("preferred")}
                onSelect={(e) => e.preventDefault()}
              >
                {t("sources.trusted_preferred")}
              </MenubarCheckboxItem>
            </MenubarSubContent>
          </MenubarSub>
          <MenubarSeparator />
          <MenubarItem disabled>{t("sources.manage")}</MenubarItem>
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  )
}
